import React, { useCallback, useEffect } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { CreateHO, Button, CancelCreateModal, useConfigureBillingModal } from '../../../components';
import { GoogleAddressHelper, LodashHelper } from '../../../utils/';
import { createHO, fetchActiveChildHOAdmins } from '../../../store/health_organizations';
import { loginAsDifferentRole } from '../../../store/authorization';
import { BackIcon } from '../../../assets';
import { usePreventLeave } from '../../../hooks/usePreventLeave';
import { createHealthOrganizationSchema } from '../../../utils/validations';
import styles from './index.module.css';
import { Navigation } from '../../../utils/navigation';
import { useLocation } from 'react-router-dom';

const initialValues = {
  name: '',
  location: {
    formattedAddress: '',
  },
  admin: {
    email: '',
    firstName: '',
    lastName: '',
    adminId: null,
  },
  selectedAdmin: null,
  rideRequestFields: {},
  parentOrgId: null,
  isEnterprise: false,
  addons: {
    uberHealthEnabled: false,
    uberHealthSurcharge: 0,
    insightsEnabled: false,
    codiEnabled: false,
  },
};

const Create_Health_Organization = ({
  defaultValues,
  createHO,
  role,
  fetchActiveChildHOAdmins,
  activeChildHOAdmins,
  loginAsDifferentRole,
}) => {
  const navigate = Navigation();

  const loc = useLocation();

  const queryParams = new URLSearchParams(loc.search);
  const parentOrgId = queryParams.get('parentOrgId');

  useEffect(() => {
    if (parentOrgId) {
      fetchActiveChildHOAdmins(parentOrgId);
    }
  }, [parentOrgId, fetchActiveChildHOAdmins]);

  // Merge initial values with any parent org ID from navigation
  const mergedInitialValues = {
    ...initialValues,
    parentOrgId: parentOrgId || null,
    addons: parentOrgId
      ? {
        ...initialValues.addons,
        insightsEnabled: true,
      }
      : initialValues.addons,
  };

  const {
    Modal: ConfigureBillingModal,
    submitCreateClinic,
  } = useConfigureBillingModal({
    title: 'Configure billing to continue',
    description: 'Billing setup is required before ride scheduling can be enabled for this organization.',
    createHO,
    loginAsDifferentRole,
    navigate,
  });

  const formik = useFormik({
    validationSchema: createHealthOrganizationSchema,
    validateOnMount: true,
    initialValues: defaultValues || mergedInitialValues,
    validateOnChange: false,
    onSubmit: (values) => {
      unblockHistory.current && unblockHistory.current();
      const trimmedValues = LodashHelper.trimValues(values);
      if (parentOrgId && activeChildHOAdmins?.length > 0) {
        submitCreateClinic(trimmedValues);
      } else {
        createHO(trimmedValues, navigate);
      }
    },
  });

  // Update title based on whether this is a clinic being added
  const pageTitle = parentOrgId ? 'Add New Facility' : 'Add New Organization';
  const instructionText = parentOrgId
    ? 'Provide information for the new Facilities'
    : 'Provide information for the new Organization';

  const [visibleModal, setModalVisible, unblockHistory, location] = usePreventLeave(mergedInitialValues, formik.values);

  const handleBack = useCallback(() => navigate.back(), [navigate]);

  const onCancel = useCallback(() => setModalVisible(false), [setModalVisible]);

  const onLeave = () => {
    if (unblockHistory.current) {
      unblockHistory.current();
      navigate.push(location.location);
    }
  };

  const handleSelectAddress = async ({ value, values, setValues }) => {
    setValues({ ...values, location: GoogleAddressHelper.transformFields(value) });
  };

  const handleChangeAddress = ({ value, values, setValues }) => {
    setValues({
      ...values,
      location: {
        formattedAddress: value,
      },
    });
  };

  return (
    <div className={styles.pageContent}>
      <div className={styles.header}>
        <div className={styles.backBtn} onClick={handleBack}>
          <BackIcon width='26px' height='26px' fill='#E1F4D3' />
        </div>
        <h1 className={styles.pageTitle}>{pageTitle}</h1>
      </div>
      <div>
        <h2>{instructionText}</h2>

        <FormikProvider value={formik}>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <CreateHO
              values={formik.values}
              handleSelectAddress={handleSelectAddress}
              handleChangeAddress={handleChangeAddress}
              parentOrgId={parentOrgId}
              activeChildHOAdmins={activeChildHOAdmins}
              role={role}
              {...formik}
            />
            <div className={styles.btnPanel}>
              <div className={styles.btnControl}>
                <Button myClassName={styles.navigationBtn} onClick={handleBack}>
                  Cancel
                </Button>
              </div>
              <div className={styles.btnControl}>
                <Button disabled={!formik.isValid || !formik.dirty} type='submit' myClassName={styles.styledBtn}>
                  {parentOrgId ? 'Create Facility' : 'Create Organization and Send Invite to Admin'}
                </Button>
              </div>
            </div>
          </form>
        </FormikProvider>
      </div>
      <CancelCreateModal
        title={parentOrgId ? 'Canceling New Facility' : 'Canceling New Organization'}
        visible={visibleModal}
        onCancel={onCancel}
        onLeave={onLeave}
      />
      {ConfigureBillingModal}
    </div>
  );
};

const mapStateToProps = (state) => (
  {
    activeChildHOAdmins: state.healthOrganizations.activeChildHOAdmins,
  });

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    createHO,
    fetchActiveChildHOAdmins,
    loginAsDifferentRole,
  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Create_Health_Organization);
